import { Maximize, ZoomIn, ZoomOut } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";

import { boxFrom, isDrawn } from "@/flow/workspace";
import type { Box } from "@/flow/workspace";
import { Button } from "@/ui/Button";
import { visibleWidth, zoomView } from "@/viewer/TypeViewer";
import type { PageSize } from "@/viewer/shapes";

const GUESS: PageSize = { w: 1600, h: 1000 };

export interface SheetGeometry {
  page: PageSize;
  /** The visible width in page pixels, for strokes and labels that keep their size. */
  viewWidth: number;
  /** A page-normalised box in the SVG's pixel space. */
  px: (b: Box) => { x: number; y: number; width: number; height: number };
}

interface Props {
  src: string;
  label: string;
  /** The window shown; changing it moves the view there. */
  window: Box;
  /** Drag draws a box instead of panning. */
  drawing?: boolean;
  onDrawn?: (box: Box) => void;
  /** The pointer's page-normalised position as it moves, or null when it leaves. */
  onPointer?: (point: [number, number] | null) => void;
  /** A click on the sheet that was not a pan, at a page-normalised point. */
  onClick?: (point: [number, number], shift: boolean) => void;
  children?: (geometry: SheetGeometry) => ReactNode;
}

/**
 * A whole sheet: the page raster as an SVG image whose view box is the part being
 * looked at, so zooming and panning change the view box and everything drawn on top
 * stays in page coordinates. Wheel zooms about the pointer; drag pans, or draws a box
 * while `drawing`.
 */
export function SheetCanvas({
  src,
  label,
  window: start,
  drawing = false,
  onDrawn,
  onPointer,
  onClick,
  children,
}: Props) {
  const svg = useRef<SVGSVGElement | null>(null);
  const [page, setPage] = useState<PageSize | null>(null);
  const [view, setView] = useState<Box>(start);
  const [frame, setFrame] = useState({ w: 0, h: 0 });
  const [draft, setDraft] = useState<Box | null>(null);
  const drag = useRef<{
    x: number;
    y: number;
    view: Box;
    from: [number, number] | null;
    moved: boolean;
  } | null>(null);

  useEffect(() => setView(start), [start]);

  useEffect(() => {
    let live = true;
    setPage(null);
    const img = new Image();
    img.onload = () => {
      if (live && img.naturalWidth > 0) {
        setPage({ w: img.naturalWidth, h: img.naturalHeight });
      }
    };
    img.src = src;
    return () => {
      live = false;
    };
  }, [src]);

  useEffect(() => {
    const el = svg.current;
    if (!el || typeof ResizeObserver === "undefined") {
      return;
    }
    const watch = new ResizeObserver(([entry]) => {
      if (entry) setFrame({ w: entry.contentRect.width, h: entry.contentRect.height });
    });
    watch.observe(el);
    return () => watch.disconnect();
  }, []);

  const size = page ?? GUESS;

  const toPage = useCallback(
    (clientX: number, clientY: number): [number, number] | null => {
      const ctm = svg.current?.getScreenCTM?.();
      if (!ctm) {
        return null;
      }
      const point = new DOMPoint(clientX, clientY).matrixTransform(ctm.inverse());
      return [point.x / size.w, point.y / size.h];
    },
    [size.w, size.h],
  );

  const zoomBy = useCallback((factor: number, at?: [number, number] | null) => {
    setView((v) => {
      const [cx, cy] = at ?? [(v.x0 + v.x1) / 2, (v.y0 + v.y1) / 2];
      return zoomView(v, factor, cx, cy);
    });
  }, []);

  useEffect(() => {
    const el = svg.current;
    if (!el) {
      return;
    }
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      zoomBy(event.deltaY > 0 ? 1.15 : 1 / 1.15, toPage(event.clientX, event.clientY));
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [toPage, zoomBy]);

  const px = (b: Box) => ({
    x: b.x0 * size.w,
    y: b.y0 * size.h,
    width: (b.x1 - b.x0) * size.w,
    height: (b.y1 - b.y0) * size.h,
  });
  const viewBox = `${view.x0 * size.w} ${view.y0 * size.h} ${(view.x1 - view.x0) * size.w} ${
    (view.y1 - view.y0) * size.h
  }`;
  const viewWidth = visibleWidth(
    (view.x1 - view.x0) * size.w,
    (view.y1 - view.y0) * size.h,
    frame.w,
    frame.h,
  );

  return (
    <div className="relative h-full w-full overflow-hidden bg-stage">
      <svg
        ref={svg}
        role="img"
        aria-label={label}
        viewBox={viewBox}
        preserveAspectRatio="xMidYMid meet"
        className="h-full w-full touch-none select-none"
        style={{ cursor: drawing ? "crosshair" : "grab" }}
        onPointerDown={(event) => {
          if (event.button !== 0) return;
          drag.current = {
            x: event.clientX,
            y: event.clientY,
            view,
            from: drawing ? toPage(event.clientX, event.clientY) : null,
            moved: false,
          };
          event.currentTarget.setPointerCapture?.(event.pointerId);
        }}
        onPointerMove={(event) => {
          const at = toPage(event.clientX, event.clientY);
          onPointer?.(at);
          const d = drag.current;
          if (!d) return;
          if (Math.abs(event.clientX - d.x) + Math.abs(event.clientY - d.y) > 3) d.moved = true;
          if (d.from && at) {
            setDraft(boxFrom(d.from, at));
            return;
          }
          const ctm = svg.current?.getScreenCTM?.();
          if (!ctm || !ctm.a) return;
          const dx = (event.clientX - d.x) / ctm.a / size.w;
          const dy = (event.clientY - d.y) / ctm.d / size.h;
          setView({
            x0: d.view.x0 - dx,
            y0: d.view.y0 - dy,
            x1: d.view.x1 - dx,
            y1: d.view.y1 - dy,
          });
        }}
        onPointerUp={(event) => {
          const d = drag.current;
          drag.current = null;
          const at = toPage(event.clientX, event.clientY);
          if (d?.from && at) {
            const box = boxFrom(d.from, at);
            setDraft(null);
            if (isDrawn(box)) onDrawn?.(box);
            return;
          }
          if (d && !d.moved && at) onClick?.(at, event.shiftKey);
        }}
        onPointerLeave={() => onPointer?.(null)}
      >
        <image
          href={src}
          x={0}
          y={0}
          width={size.w}
          height={size.h}
          preserveAspectRatio="none"
          opacity={page ? 1 : 0}
        />
        {children?.({ page: size, viewWidth, px })}
        {draft ? (
          <rect
            {...px(draft)}
            fill="var(--color-accent)"
            fillOpacity={0.1}
            stroke="var(--color-accent)"
            strokeDasharray="6 4"
            strokeWidth={2}
            vectorEffect="non-scaling-stroke"
            data-draft
          />
        ) : null}
      </svg>
      <div className="absolute right-3 bottom-3 flex gap-1 rounded-lg border border-line bg-surface/95 p-1 shadow-card">
        <Button
          size="sm"
          variant="ghost"
          className="w-8 px-0"
          aria-label="Zoom in"
          onClick={() => zoomBy(1 / 1.4)}
        >
          <ZoomIn aria-hidden="true" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="w-8 px-0"
          aria-label="Zoom out"
          onClick={() => zoomBy(1.4)}
        >
          <ZoomOut aria-hidden="true" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="w-8 px-0"
          aria-label="Fit"
          onClick={() => setView(start)}
        >
          <Maximize aria-hidden="true" />
        </Button>
      </div>
    </div>
  );
}
